import type {
  OrganizationTreeCardOption,
  OrganizationTreeCardState,
} from "./organization-tree-card.type";
import type { OrganizationTreeOptions } from "@/modules/organization/organization-tree";

import { Form } from "antd";
import { useEffect, useState } from "react";

import { useGetCurrentUserOrganization } from "@/entities/current-user";

import {
  getOrganizationFromStorage,
  saveOrganizationInStorage,
} from "./organization-tree-card.helper";

export const FIELD_NAME = "organization_id";

const useOrganizationTreeCardInitValue = ({
  saveStateKey,
}: OrganizationTreeOptions) => {
  const savedOrganization = getOrganizationFromStorage({ saveStateKey });

  const {
    organization: userOrganization,
    isLoading: isUserOrganizationLoading,
    userQueryResult: { isLoading: isUserLoading },
  } = useGetCurrentUserOrganization({ enabled: !savedOrganization });

  const isLoading = isUserLoading || isUserOrganizationLoading;

  return savedOrganization
    ? { organization: savedOrganization, isLoading: false }
    : { organization: userOrganization, isLoading };
};

export const useOrganizationTreeCard = ({
  saveStateKey,
}: OrganizationTreeOptions) => {
  const [form] = Form.useForm();

  const { organization: initValue, isLoading } =
    useOrganizationTreeCardInitValue({ saveStateKey });

  const [isInited, setIsInited] = useState(false);

  const [organization, setOrganization] =
    useState<OrganizationTreeCardState>(undefined);

  // Triggered when user changes the organization in select
  const onOrganizationChange = (
    option: OrganizationTreeCardOption | OrganizationTreeCardOption[]
  ) => {
    if (Array.isArray(option)) return;

    const newOrganization = option.value
      ? { id: option.value, name: option.label }
      : undefined;

    setOrganization(newOrganization);

    saveOrganizationInStorage({
      saveStateKey,
      organization: newOrganization,
    });
  };

  useEffect(() => {
    if (!isInited && initValue) {
      setOrganization(initValue);
      setIsInited(true);
    }
  }, [isInited, initValue]);

  // Update organization id in form with the name
  useEffect(() => {
    if (!isInited) return;

    form.resetFields([FIELD_NAME]);

    if (organization)
      form.setFieldsValue({
        organization_id: {
          value: organization.id,
          label: organization.name,
          key: organization.id,
        },
      });
  }, [isInited, organization]);

  return {
    organization,
    isLoading,
    form,
    fieldName: FIELD_NAME,
    onOrganizationChange,
  };
};
